import React, { useState, useEffect } from 'react';
import moment from 'moment';
import dayjs from 'dayjs';
import { trim } from 'lodash';
import { Popover, Button, DatePicker, Input } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faTimes } from '@fortawesome/pro-regular-svg-icons';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import RadioButttonWithIcons, {
  getAlertRadioOptions,
  durationRadioOptions
} from '../../RadioButttonWithIcons';
import style from './index.scss';

const { RangePicker } = DatePicker;
const dateFormat = 'DD/MM/YYYY';

export const Filter = (props) => {
  const { GS, filters, alertLevels } = props;
  const [visible, setVisible] = useState(false);
  const [filteredKeys, setFilteredKeys] = useState([]);
  const [values, setValues] = useState([]);
  const [formValues, setFormValues] = useState({});
  const [dateSelectedFrom, setDateSelectedFrom] = useState('');
  const [errors, setErrors] = useState({});
  const [disableBtn, setDisableBtn] = useState(true);

  const filterlabel = [
    { id: 'alertDate', name: 'Alert Date' },
    { id: 'alertLevel', name: 'Alert Level' },
    { id: 'runInstanceId', name: 'Run Instance ID' },
    { id: 'runInstanceExecutionId', name: 'Run Instance Execution ID' }
  ];

  useEffect(() => {
    if (GS?.data !== null) getFilterableKeys();
  }, []);

  const show = () => {
    setVisible(true);
  };

  const hide = () => {
    setVisible(false);
  };

  const validatingRegex = (pattern, value) => {
    let regex = new RegExp(trim(pattern, '/'));
    return regex.test(value.trimEnd());
  };

  const handleVisibleChange = (vis) => {
    setVisible(vis);
  };

  const clearFilterHandler = () => {
    setErrors({});
    setFormValues({});
    filteredKeys.forEach((element) => {
      if (element.dataType.toLowerCase() === 'string')
        pushToQueryParam('', element.itemName);
    });
    checkforErrors({});
    setValues([]);
    if (filters?.filter?.length || filters?.from || filters?.to) {
      props.applyFilterHandler({
        from: undefined,
        to: undefined,
        filter: []
      });
      setVisible(false);
    }
  };

  const applyFilterHandler = () => {
    let finalArr = values;
    let dateKeys = filteredKeys.filter((item) => item.dataType === 'DATE');
    let dates = {
      from: undefined,
      to: undefined
    };
    if (dateKeys.length > 0) {
      let key = dateKeys[0]?.itemName;

      if (formValues[key])
        dates = {
          from: moment(formValues?.[key][0])
            .startOf('day')
            .format('YYYY-MM-DD[T]HH:mm:ss.000[Z]'),
          to: moment(formValues?.[key][1])
            .endOf('day')
            .format('YYYY-MM-DD[T]HH:mm:ss.999[Z]')
        };
    }

    props.applyFilterHandler({
      ...dates,
      filter: finalArr
    });
    setVisible(false);
  };

  const pushToQueryParam = (value, key, type) => {
    let ar = [...values];
    let isKeyAvailable = false;
    let obj = {
      columnName: key,
      logic: type === 'textbox' ? 'LIKE' : '=',
      value: value
    };
    if (ar.length == 0) {
      ar.push(obj);
    } else {
      ar.forEach((element, index) => {
        if (key === element.columnName) {
          isKeyAvailable = true;
          ar[index] = obj;
        }
      });
      if (!isKeyAvailable) ar.push(obj);
    }
    setValues(ar);
  };

  useEffect(() => {
    const areValuesEmpty =
      isEmpty(values) || values.every(({ value }) => value === '');
    const areFormValuesEmpty =
      isEmpty(formValues) ||
      Object.values(formValues).every((value) => value === '');

    if ((!areValuesEmpty || !areFormValuesEmpty) && isEmpty(errors)) {
      setDisableBtn(false);
    } else {
      setDisableBtn(true);
    }
  }, [values, errors, formValues]);

  const onCalendarChange = (momentDates, dates, key) => {
    setDateSelectedFrom('CALENDER');
    setFormValues({
      ...formValues,
      [key]: momentDates
    });
  };

  const onRadioInputChange = (value, key, type) => {
    if (type === 'DATE') {
      setDateSelectedFrom('RADIO');
      let startDate = moment().subtract(value, 'days');
      let endDate = moment();

      setFormValues({
        ...formValues,
        [key]: [startDate, endDate]
      });
    } else {
      pushToQueryParam(value, key);
      setFormValues({
        ...formValues,
        [key]: value
      });
    }
  };

  const onInputChange = (value, key) => {
    let newError = { ...errors };

    if (!!value && !/^\d*$/.test(value)) {
      // for space and special characters in starting of string
      newError = {
        ...newError,
        [key]: `Numbers only allowed(0-9).`
      };
    } else if (value?.length > 100) {
      newError = {
        ...newError,
        [key]: `Maximum 100 characters only allowed.`
      };
    } else {
      delete newError[key];
    }

    checkforErrors(newError);
    setErrors(newError);
    pushToQueryParam(value, key, 'textbox');
    setFormValues({
      ...formValues,
      [key]: value
    });
  };

  const checkforErrors = (err) => {
    for (const key in err) {
      if (err[key] !== '') {
        setDisableBtn(true);
        return;
      }
    }
    setDisableBtn(false);
  };

  const getFilterableKeys = () => {
    const ar = [];
    GS?.data?.widgetItems[0]?.itemsOperations.map((item) => {
      if (item.isFilterable) {
        ar.push(item);
      }
    });
    setFilteredKeys(ar);
  };

  const getFilterLabel = (key) => {
    return filterlabel.filter((item) => item.id === key)[0].name;
  };

  const disabledDate = (current) => {
    // Can not select days after today
    return current > dayjs().endOf('day');
  };

  const renderFilterItemByGlobalSettingKeys = (item, index) => {
    if (item.itemName === 'alertLevel') {
      return (
        <div className={style.filterItem}>
          <label>{getFilterLabel(item.itemName)}</label>
          <RadioButttonWithIcons
            options={getAlertRadioOptions(alertLevels)}
            defaultValue={
              formValues?.[item.itemName] ? formValues?.[item.itemName] : ''
            }
            onChangeHandler={(res) => onRadioInputChange(res, item.itemName)}
          />
        </div>
      );
    }
    switch (item.dataType) {
      case 'DATE':
        return (
          <React.Fragment key={index}>
            <div className={style.filterItem}>
              <label>Duration</label>
              <RadioButttonWithIcons
                options={durationRadioOptions}
                defaultValue={
                  formValues?.[item.itemName] && dateSelectedFrom === 'RADIO'
                    ? formValues?.[item.itemName]
                    : ''
                }
                onChangeHandler={(selectedValue) =>
                  onRadioInputChange(selectedValue, item.itemName, 'DATE')
                }
              />
            </div>

            <div className={style.filterItem}>
              <label id="rangePickerLabel">Date Range</label>
              <RangePicker
                disabledDate={disabledDate}
                value={
                  dateSelectedFrom === 'CALENDER' || 'RADIO'
                    ? formValues?.[item.itemName]
                    : ''
                }
                onCalendarChange={(m, d) =>
                  onCalendarChange(m, d, item.itemName)
                }
                onChange={(m, d) => onCalendarChange(m, d, item.itemName)}
                inputReadOnly={true}
                format={dateFormat}
                size="small"
                style={{ cursor: 'pointer', width: '256px' }}
              />
            </div>
          </React.Fragment>
        );
      case 'STRING':
        return (
          <div className={style.filterItem}>
            <label>{getFilterLabel(item.itemName)}</label>
            <Input
              name={item.itemName}
              value={formValues?.[item.itemName]}
              onChange={(e) => onInputChange(e.target.value, e.target.name)}
              autoComplete="off"
            />
            {errors?.[item.itemName] !== '' && (
              <p className={style.errorMessage}>{errors?.[item.itemName]}</p>
            )}
          </div>
        );
    }
  };

  const isButtonDisabled = isEmpty(errors) ? disableBtn : false;

  return (
    <div className={style.wrapper}>
      <p className={style.iconLabel}>Filter</p>
      <Popover
        content={
          <div className={style.popover} id="filterPopOver">
            <div className={style.popover_head}>
              <h6>Filter</h6>
              <div id="faTimesCloseIcon" onClick={hide}>
                <FontAwesomeIcon icon={faTimes} />
              </div>
            </div>
            <div className={style.popover_content}>
              {filteredKeys.length > 0
                ? filteredKeys.map((item, index) =>
                    renderFilterItemByGlobalSettingKeys(item, index)
                  )
                : null}
            </div>
            <div className={style.popover_footer}>
              <Button onClick={clearFilterHandler} disabled={isButtonDisabled}>
                Clear
              </Button>
              <Button
                onClick={applyFilterHandler}
                disabled={disableBtn}
                type="primary">
                Apply
              </Button>
            </div>
          </div>
        }
        visible={visible}
        onVisibleChange={handleVisibleChange}
        trigger="click"
        placement="bottomLeft">
        <div className={style.iconWrapper} id="faFilterIcon" onClick={show}>
          <FontAwesomeIcon icon={faFilter} />
        </div>
      </Popover>
    </div>
  );
};

Filter.propTypes = {
  GS: PropTypes.object,
  applyFilterHandler: PropTypes.func,
  filters: PropTypes.object
};

export default Filter;
